import { useState, useEffect } from 'react';
import { useAuth } from '@hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, 
  MessageCircle, 
  Map, 
  Users, 
  UserPlus, 
  TrendingUp,
  CheckCircle,
  Clock,
  Award,
  Target,
  Activity,
  BarChart3,
  Briefcase,
  Calendar,
  ArrowRight,
  Sparkles,
  Zap,
  HelpCircle
} from 'lucide-react';
import Card from '@components/common/Card';
import Button from '@components/common/Button';
import dashboardService from '@services/dashboard.service';
import toast from 'react-hot-toast';
import { ROUTES } from '@constants';

const DashboardNew = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentActivities, setRecentActivities] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
    
      const statsResponse = await dashboardService.getDashboardStats();
      if (statsResponse?.success) {
        setStats(statsResponse.data);
      } else {
        console.log('Stats response not successful:', statsResponse);
      }
      
      const activitiesResponse = await dashboardService.getRecentActivities(5);
      if (activitiesResponse?.success) {
        setRecentActivities(activitiesResponse.data);
      } else {
        console.log('Activities response not successful:', activitiesResponse);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      console.error('Error details:', error.response || error.message);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Compact, mobile-first stat card — smaller padding/icon/text on small screens
  const StatCard = ({ icon: Icon, title, value, subtitle, color, trend, onClick }) => (
    <Card 
      className={`p-3 sm:p-5 hover:shadow-lg transition-all duration-300 cursor-pointer border-l-4 ${color}`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between gap-1">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Icon className={`w-4 h-4 flex-shrink-0 ${color.replace('border-', 'text-')}`} />
            <p className="text-[11px] sm:text-sm font-medium text-gray-600 truncate">{title}</p>
          </div>
          <h3 className="text-xl sm:text-3xl font-bold text-gray-900 mb-0.5">{value}</h3>
          {subtitle && (
            <p className="text-[10px] sm:text-xs text-gray-500 truncate">{subtitle}</p>
          )}
        </div>
        {trend != null && (
          <div className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] sm:text-xs font-medium flex-shrink-0 ${
            trend > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
          }`}>
            <TrendingUp className="w-3 h-3" />
            {trend > 0 && '+'}
            {trend}
          </div>
        )}
      </div>
    </Card>
  );

  // Compact quick-action card — sits 2-up on mobile instead of 1-up
  const QuickActionCard = ({ icon: Icon, title, description, color, onClick }) => (
    <Card 
      className="p-3 sm:p-4 hover:shadow-lg transition-all duration-300 cursor-pointer group"
      onClick={onClick}
    >
      <div className={`w-9 h-9 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl ${color} flex items-center justify-center mb-2 sm:mb-3 group-hover:scale-110 transition-transform`}>
        <Icon className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
      </div>
      <h3 className="text-xs sm:text-base font-bold text-gray-900 mb-0.5 sm:mb-1 leading-tight">{title}</h3>
      <p className="text-[10px] sm:text-xs text-gray-600 mb-2 sm:mb-3 line-clamp-2">{description}</p>
      <div className="flex items-center text-[10px] sm:text-xs font-medium text-primary-600 group-hover:text-primary-700">
        Get Started <ArrowRight className="w-3 h-3 ml-1 group-hover:translate-x-1 transition-transform" />
      </div>
    </Card>
  );

  const ActivityItem = ({ icon: Icon, title, description, time, color }) => (
    <div className="flex items-start gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors">
      <div className={`w-10 h-10 rounded-full ${color} flex items-center justify-center flex-shrink-0`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{title}</p>
        <p className="text-xs text-gray-600 truncate">{description}</p>
        <p className="text-xs text-gray-400 mt-1">{time}</p>
      </div>
    </div>
  );

  // Compact network-stat card — 3-up on mobile instead of stacked full-width
  const NetworkStatCard = ({ icon: Icon, value, label, iconColor }) => (
    <Card className="p-3 sm:p-5 text-center">
      <Icon className={`w-5 h-5 sm:w-8 sm:h-8 ${iconColor} mx-auto mb-1 sm:mb-2`} />
      <h3 className="text-lg sm:text-2xl font-bold text-gray-900">{value}</h3>
      <p className="text-[10px] sm:text-sm text-gray-600">{label}</p>
    </Card>
  );

  if (loading) {
    return (
      <div className="w-full">
        <div className="mb-6">
          <div className="h-8 bg-gray-200 rounded w-64 mb-2 animate-pulse"></div>
          <div className="h-4 bg-gray-200 rounded w-96 animate-pulse"></div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl shadow-sm p-4 sm:p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-24 mb-4"></div>
              <div className="h-8 bg-gray-200 rounded w-16 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-32"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const overview = stats?.overview || {};
  const recentActivity = stats?.recentActivity || {};
  const userPlan = stats?.userPlan || {};

  return (
    <div className="w-full space-y-5 sm:space-y-6">
      {/* Main Stats Grid — 2-up on mobile, 4-up from desktop */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard
          icon={FileText}
          title="Career Reports"
          value={overview.totalReports || 0}
          subtitle="Reports generated"
          color="border-blue-500"
          trend={recentActivity.recentReports}
          onClick={() => navigate(ROUTES.STUDENT_CAREER_REPORT)}
        />
        
        <StatCard
          icon={MessageCircle}
          title="Mentor Questions"
          value={overview.totalQuestions || 0}
          subtitle={`${overview.answeredQuestions || 0} answered`}
          color="border-purple-500"
          trend={recentActivity.recentQuestions}
          onClick={() => navigate(ROUTES.STUDENT_MY_QUESTIONS)}
        />
        
        <StatCard
          icon={Map}
          title="Career Maps"
          value={overview.completedMaps || 0}
          subtitle={`${overview.inProgressMaps || 0} in progress`}
          color="border-green-500"
          trend={recentActivity.recentMapProgress}
          onClick={() => navigate(ROUTES.STUDENT_CAREER_MAP)}
        />
        
        <StatCard
          icon={Users}
          title="Connections"
          value={overview.connectionsCount || 0}
          subtitle={`${overview.followersCount || 0} followers`}
          color="border-orange-500"
          onClick={() => navigate('/students/myConnection')}
        />
      </div>

      {/* Progress Cards */}
      <div className="space-y-3 sm:space-y-4">
        <h2 className="text-base sm:text-lg font-bold text-gray-900 flex items-center gap-2">
          <Target className="w-4 h-4 sm:w-5 sm:h-5 text-primary-600" />
          Your Progress
        </h2>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          {/* Portfolio Completion */}
          <Card className="p-4 sm:p-5">
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <div className="flex items-center gap-2">
                <Briefcase className="w-4 h-4 sm:w-5 sm:h-5 text-primary-600" />
                <h3 className="text-xs sm:text-sm font-semibold text-gray-900">Portfolio</h3>
              </div>
              <span className="text-base sm:text-lg font-bold text-primary-600">
                {overview.portfolioCompletion || 0}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
              <div 
                className="bg-gradient-to-r from-primary-500 to-primary-600 h-2 rounded-full transition-all duration-500"
                style={{ width: `${overview.portfolioCompletion || 0}%` }}
              ></div>
            </div>
            <p className="text-[10px] sm:text-xs text-gray-600">
              {overview.portfolioCompletion >= 100 ? 'Completed!' : 'Keep building your profile'}
            </p>
          </Card>

          {/* Career Clarity Score */}
          <Card className="p-4 sm:p-5">
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600" />
                <h3 className="text-xs sm:text-sm font-semibold text-gray-900">Clarity Score</h3>
              </div>
              <span className="text-base sm:text-lg font-bold text-yellow-600">
                {overview.clarityScore || 0}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
              <div 
                className="bg-gradient-to-r from-yellow-400 to-yellow-600 h-2 rounded-full transition-all duration-500"
                style={{ width: `${overview.clarityScore || 0}%` }}
              ></div>
            </div>
            <p className="text-[10px] sm:text-xs text-gray-600">
              {overview.clarityScore >= 80 ? 'Excellent clarity!' : 'Complete your career report'}
            </p>
          </Card>
        </div>
      </div>

      {/* Quick Actions — 2-up on mobile instead of full-width stack */}
      <div className="space-y-3 sm:space-y-4">
        <h2 className="text-base sm:text-lg font-bold text-gray-900 flex items-center gap-2">
          <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-primary-600" />
          Quick Actions
        </h2>
        
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <QuickActionCard
            icon={FileText}
            title="Generate Report"
            description="Create a new career clarity report"
            color="bg-gradient-to-br from-blue-500 to-blue-600"
            onClick={() => navigate(ROUTES.STUDENT_CAREER_REPORT)}
          />
          
          <QuickActionCard
            icon={MessageCircle}
            title="Ask Mentor"
            description="Get expert guidance from mentors"
            color="bg-gradient-to-br from-purple-500 to-purple-600"
            onClick={() => navigate('/student/mentor')}
          />
          
          <QuickActionCard
            icon={Briefcase}
            title="Update Portfolio"
            description="Showcase your skills and projects"
            color="bg-gradient-to-br from-green-500 to-green-600"
            onClick={() => navigate('/profile')}
          />
          
          <QuickActionCard
            icon={Users}
            title="Browse Students"
            description="Connect with other professionals"
            color="bg-gradient-to-br from-orange-500 to-orange-600"
            onClick={() => navigate('/students')}
          />
        </div>
      </div>

      {/* Network Stats — 3-up even on mobile instead of stacked full-width */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        <NetworkStatCard
          icon={Users}
          value={overview.followersCount || 0}
          label="Followers"
          iconColor="text-blue-600"
        />
        <NetworkStatCard
          icon={UserPlus}
          value={overview.followingCount || 0}
          label="Following"
          iconColor="text-green-600"
        />
        <NetworkStatCard
          icon={Award}
          value={overview.connectionsCount || 0}
          label="Connections"
          iconColor="text-purple-600"
        />
      </div>
    </div>
  );
};

export default DashboardNew;